/** Pending, definite failed and uncertain drafts live outside refetchable caches.
 * Keys include actor/workspace/session revision, plus channel; thread selectors
 * preserve reply drafts too. Scope changes clear memory, JWT/locale changes keep
 * drafts. This is not durable storage or a server idempotency ledger. */
import { create } from 'zustand';
import type { ChannelMessage } from './queries';
import { useAuthStore } from '@/store/auth-store';
import { sessionScope } from '@/features/auth/session-scope';
import { captureChatOwner } from './creation-actions';
import { isCurrentMessageOwner, type MessageOwner } from './message-cache';

interface FailedOutboxState {
  /** Encoded owner/revision/channel → complete local rows (FIFO). */
  byChannel: Record<string, ChannelMessage[]>;
  addFailed: (channelId: string, msg: ChannelMessage) => void;
  removeFailed: (channelId: string, tmpId: string) => void;
}

/** Stable empty array so the zustand selector doesn't re-render forever. */
const EMPTY: ChannelMessage[] = [];

export const useFailedOutboxStore = create<FailedOutboxState>((set) => ({
  byChannel: {},
  addFailed: (channelId, msg) =>
    set((s) => {
      const prev = s.byChannel[channelId] ?? EMPTY;
      // Dedupe by id: re-adding an existing tmp id replaces the stored row
      // (keeps its queue position) instead of duplicating it.
      const next =
        msg.id && prev.some((m) => m.id === msg.id)
          ? prev.map((m) => (m.id === msg.id ? msg : m))
          : [...prev, msg];
      return { byChannel: { ...s.byChannel, [channelId]: next } };
    }),
  removeFailed: (channelId, tmpId) =>
    set((s) => {
      const prev = s.byChannel[channelId];
      if (!prev || !prev.some((m) => m.id === tmpId)) return s;
      const next = prev.filter((m) => m.id !== tmpId);
      const byChannel = { ...s.byChannel };
      if (next.length > 0) {
        byChannel[channelId] = next;
      } else {
        delete byChannel[channelId];
      }
      return { byChannel };
    }),
}));

/** Non-React entry points (mutation callbacks in queries.ts). */
function outboxKey(channelId: string, owner: MessageOwner) { return JSON.stringify([owner.scope, owner.revision, channelId]); }
useAuthStore.subscribe((next,previous) => {
  if(sessionScope(next)!==sessionScope(previous)) useFailedOutboxStore.setState({byChannel:{}});
});
export function addFailed(channelId: string, msg: ChannelMessage, owner = captureChatOwner()): void {
  if(isCurrentMessageOwner(owner)) useFailedOutboxStore.getState().addFailed(outboxKey(channelId,owner), msg);
}

export function removeFailed(channelId: string, tmpId: string, owner = captureChatOwner()): void {
  if(isCurrentMessageOwner(owner)) useFailedOutboxStore.getState().removeFailed(outboxKey(channelId,owner), tmpId);
}

/** Pure selector — exported for tests and non-hook reads. */
export function selectFailedMessages(
  state: Pick<FailedOutboxState, 'byChannel'>,
  channelId: string,
  owner = captureChatOwner(),
): ChannelMessage[] {
  return isCurrentMessageOwner(owner) ? state.byChannel[outboxKey(channelId,owner)] ?? EMPTY : EMPTY;
}

/** Local rows for a captured channel/root or reply surface. */
export function useFailedMessages(channelId: string, parentMessageId?: string): ChannelMessage[] {
  useAuthStore(sessionScope);
  const owner=captureChatOwner();
  const rows=useFailedOutboxStore((s) => selectFailedMessages(s, channelId, owner));
  return rows.filter(row => row.parent_message_id === parentMessageId);
}
